import { type FC, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useGetEnrollmentSuitabilityData, useSubmitEnrollmentSuitabilityData } from '@egw/api'
import { Button, GenericError, Input, RadioGroup } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import { ROUTES } from '../../../constants'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Personal Info')

interface FormData {
  directorPolicyMakerOfCompany: string
  isBrokerDealer: string
  isDirectorPolicyMakerOfCompany: string
}

export const SuitabilityPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentSuitabilityData()
  const { mutate: submit, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentSuitabilityData()

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      directorPolicyMakerOfCompany: '',
      isBrokerDealer: Number(data?.enrollmentData.userInfo.investment.isBrokerDealer),
      isDirectorPolicyMakerOfCompany: Number(data?.enrollmentData.userInfo.investment.isDirectorPolicyMakerOfCompany),
    },
  })

  useEffect(() => {
    if (data?.enrollmentData.userInfo.investment.isBrokerDealer !== undefined) {
      reset({
        directorPolicyMakerOfCompany: data.enrollmentData.userInfo.investment.directorPolicyMakerOfCompany ?? '',
        isBrokerDealer: data.enrollmentData.userInfo.investment.isBrokerDealer ? 1 : 0,
        isDirectorPolicyMakerOfCompany: data.enrollmentData.userInfo.investment.isDirectorPolicyMakerOfCompany ? 1 : 0,
      })
    }
  }, [data?.enrollmentData.userInfo, reset])

  const isShowCompanyNameInput = watch('isDirectorPolicyMakerOfCompany')
  const isShowBrokerDealerInput = watch('isBrokerDealer')

  useValidateRoute(data?.path)

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData

    const payload = {
      directorPolicyMakerOfCompany: formData.isDirectorPolicyMakerOfCompany
        ? formData.directorPolicyMakerOfCompany
        : undefined,
      isBrokerDealer: Boolean(formData.isBrokerDealer),
      isDirectorPolicyMakerOfCompany: Boolean(formData.isDirectorPolicyMakerOfCompany),
    }

    try {
      const response = await submit(payload)

      if (response?.path) {
        navigate(response.path)
      }
    } catch (err) {
      log.error('Error submitting enrollment intro data', err)
    }
  })

  return (
    <EnrollmentLayout title="Suitability">
      <div className="egw-container-enroll h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError ? (
          <>
            <PageHeader title={t('investments.personal-info.title')} />

            <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col h-full">
              <p className="mb-md typo-primary-sm-regular text-text-meta">
                {t('investments.personal-info.description')}
              </p>
              <p className="typo-primary-base-semibold mb-sm">{t('investments.personal-info.broker_dealer_title')}</p>
              <p className="typo-primary-sm-regular text-text-body">
                {t('investments.personal-info.broker_dealer_description')}
              </p>

              <Controller
                name="isBrokerDealer"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => !isNaN(value) || 'Must be a valid number',
                }}
                render={({ field: { ref, ...restField } }) => (
                  <RadioGroup
                    {...restField}
                    value={restField.value}
                    options={[
                      { value: 0, label: t('common.no') },
                      { value: 1, label: t('common.yes') },
                    ]}
                    name="isBrokerDealer"
                    className="mt-md"
                    testId="is-broker-dealer-radio"
                  />
                )}
              />

              {isShowBrokerDealerInput ? (
                <p className="typo-primary-sm-regular text-text-body mb-sm">
                  {t('investments.personal-info.is_yes_broker_dealer')}
                </p>
              ) : null}

              <p className="typo-primary-base-semibold mt-md mb-sm">
                {t('investments.personal-info.major_share_holder_title')}
              </p>
              <p className="typo-primary-sm-regular text-text-body">
                {t('investments.personal-info.major_share_holder_description')}
              </p>

              <Controller
                name="isDirectorPolicyMakerOfCompany"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => !isNaN(value) || 'Must be a valid number',
                }}
                render={({ field: { ref, ...restField } }) => (
                  <RadioGroup
                    {...restField}
                    value={restField.value}
                    options={[
                      { value: 0, label: t('common.no') },
                      { value: 1, label: t('common.yes') },
                    ]}
                    name="isDirectorPolicyMakerOfCompany"
                    className="mt-md"
                    testId="is-director-policy-maker-of-company-radio"
                  />
                )}
              />

              {isShowCompanyNameInput ? (
                <>
                  <p className="typo-primary-sm-regular text-text-body">
                    {t('investments.personal-info.is_yes_major_share_holder')}
                  </p>
                  <Controller
                    name="directorPolicyMakerOfCompany"
                    control={control}
                    rules={{
                      required: true,
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder={t('investments.personal-info.company_name_and_ticker')}
                        name="directorPolicyMakerOfCompany"
                        testId="enrollment-name-company-input"
                        className="my-sm"
                      />
                    )}
                  />
                </>
              ) : null}

              <Button
                type="submit"
                className="w-full md:w-min mt-lg"
                isDisabled={!isValid}
                isLoading={isSubmitting}
                testId="enrollment-continue-button"
              >
                {t('common.continue')}
              </Button>
            </form>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentLayout>
  )
}
